import { Component } from '@theme/component';
import { DialogCloseEvent } from '@theme/dialog';

/**
 * Background MP4 with a YouTube embed opened in a modal from the play control.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} modal
 * @property {HTMLButtonElement} playButton
 * @property {HTMLButtonElement} closeButton
 * @property {HTMLElement} youtubeContainer
 *
 * @extends {Component<Refs>}
 */
class YoutubeBackgroundVideoComponent extends Component {
  /** @type {AbortController | undefined} */
  #abortController;

  connectedCallback() {
    super.connectedCallback();
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    this.youtubeId = this.dataset.youtubeId?.trim() ?? '';

    this.refs.playButton?.addEventListener('click', this.#handlePlayClick, { signal });
    this.refs.closeButton?.addEventListener('click', this.#closeModal, { signal });
    this.refs.modal?.addEventListener('click', this.#handleModalClick, { signal });
    document.addEventListener('keydown', this.#handleKeydown, { signal });
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    this.#abortController = undefined;
    super.disconnectedCallback();
  }

  /**
   * @param {Event} event
   */
  #handlePlayClick = (event) => {
    event.preventDefault();
    this.#openModal();
  };

  #handleModalClick = (event) => {
    if (event.target === this.refs.modal) this.#closeModal();
  };

  /**
   * @param {KeyboardEvent} event
   */
  #handleKeydown = (event) => {
    if (event.key !== 'Escape') return;
    if (!this.refs.modal?.classList.contains('youtube-bg-video__modal--open')) return;
    this.#closeModal();
  };

  #openModal() {
    if (!this.youtubeId || !this.refs.modal || !this.refs.youtubeContainer) return;

    if (!this.refs.youtubeContainer.querySelector('iframe')) {
      const iframe = document.createElement('iframe');
      iframe.setAttribute(
        'src',
        `https://www.youtube.com/embed/${this.youtubeId}?autoplay=1&controls=1&modestbranding=1&rel=0`
      );
      iframe.setAttribute('title', 'YouTube video');
      iframe.setAttribute('allow', 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture');
      iframe.setAttribute('allowfullscreen', '');
      this.refs.youtubeContainer.appendChild(iframe);
    }

    this.refs.modal.classList.add('youtube-bg-video__modal--open');
    document.body.style.overflow = 'hidden';
  }

  #closeModal = () => {
    if (!this.refs.modal || !this.refs.youtubeContainer) return;

    this.refs.modal.classList.remove('youtube-bg-video__modal--open');
    document.body.style.overflow = '';

    const iframe = this.refs.youtubeContainer.querySelector('iframe');
    iframe?.remove();

    window.dispatchEvent(new DialogCloseEvent());
  };
}

if (!customElements.get('youtube-background-video-component')) {
  customElements.define('youtube-background-video-component', YoutubeBackgroundVideoComponent);
}
